import { createSlice } from '@reduxjs/toolkit'
import {
    CatalogResponseDTO,
    CompanyChildDTO,
    CompanyDTO,
    CompanyIdDTO,
    RoleDTO,
    SiteResponseDTO,
    UserDTO,
    UserResponseDTO
} from '../../Management/ManagementModel'
import { FetchStatusType } from '../storeType'
import {
    createUser,
    deleteUser,
    exportAllUsers,
    exportCatalog,
    exportCatalogById,
    exportSiteById,
    exportSiteCompany,
    getAllCatalogs,
    getAllCompanies,
    getAllCompaniesChildren,
    getAllRoles,
    getAllSites,
    getAllUsers,
    getByIdCompany,
    sendResetPwd,
    updateCatalogDM,
    updateCatalogYT,
    updateUser
} from '../../Actions/ManagementAction'
import { RootState } from '..'

interface ManagementState {
    listCompanies: CompanyDTO[]
    companyById: CompanyIdDTO
    companiesChildren: CompanyChildDTO[]
    listUsers: UserDTO[]
    updateUser: UserResponseDTO
    listSites: SiteResponseDTO[]
    listCatalogs: CatalogResponseDTO[]
    listRoles: RoleDTO[]
    loadingCompanies: FetchStatusType
    loadingCompanyId: FetchStatusType
    loadingCompaniesChildren: FetchStatusType
    loadingListUsers: FetchStatusType
    loadingCreateUser: FetchStatusType
    loadingUpdateUser: FetchStatusType
    loadingDeleteUser: FetchStatusType
    loadingExportAllUser: FetchStatusType
    loadingGetSites: FetchStatusType
    loadingExportSite: FetchStatusType
    loadingExportSiteById: FetchStatusType
    loadingGetCatalog: FetchStatusType
    loadingDM: FetchStatusType
    loadingYT: FetchStatusType
    loadingExportCatById: FetchStatusType
    loadingExportCat: FetchStatusType
    loadingListRole: FetchStatusType
    loadingResetMdp: FetchStatusType
}
const initialState: ManagementState = {
    listCompanies: [],
    companyById: {} as CompanyIdDTO,
    companiesChildren: [],
    listUsers: [],
    updateUser: {} as UserResponseDTO,
    listSites: [],
    listCatalogs: [],
    listRoles: [],
    loadingCompanies: FetchStatusType.IDLE,
    loadingCompanyId: FetchStatusType.IDLE,
    loadingCompaniesChildren: FetchStatusType.IDLE,
    loadingListUsers: FetchStatusType.IDLE,
    loadingCreateUser: FetchStatusType.IDLE,
    loadingUpdateUser: FetchStatusType.IDLE,
    loadingDeleteUser: FetchStatusType.IDLE,
    loadingExportAllUser: FetchStatusType.IDLE,
    loadingGetSites: FetchStatusType.IDLE,
    loadingExportSite: FetchStatusType.IDLE,
    loadingExportSiteById: FetchStatusType.IDLE,
    loadingGetCatalog: FetchStatusType.IDLE,
    loadingDM: FetchStatusType.IDLE,
    loadingYT: FetchStatusType.IDLE,
    loadingExportCatById: FetchStatusType.IDLE,
    loadingExportCat: FetchStatusType.IDLE,
    loadingListRole: FetchStatusType.IDLE,
    loadingResetMdp: FetchStatusType.IDLE
}
export const ManagementSlice = createSlice({
    name: 'management',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(getAllCompanies.pending, state => {
                return { ...state, loadingCompanies: FetchStatusType.LOADING }
            })
            .addCase(getAllCompanies.fulfilled, (state, action) => {
                return { ...state, loadingCompanies: FetchStatusType.COMPLETED, listCompanies: action.payload }
            })
            .addCase(getAllCompanies.rejected, state => {
                return { ...state, loadingCompanies: FetchStatusType.FAILED }
            })
        builder
            .addCase(getByIdCompany.pending, state => {
                return { ...state, loadingCompanyId: FetchStatusType.LOADING }
            })
            .addCase(getByIdCompany.fulfilled, (state, action) => {
                return { ...state, loadingCompanyId: FetchStatusType.COMPLETED, companyById: action.payload }
            })
            .addCase(getByIdCompany.rejected, state => {
                return { ...state, loadingCompanyId: FetchStatusType.FAILED }
            })
        builder
            .addCase(getAllCompaniesChildren.pending, state => {
                return { ...state, loadingCompaniesChildren: FetchStatusType.LOADING }
            })
            .addCase(getAllCompaniesChildren.fulfilled, (state, action) => {
                return { ...state, loadingCompaniesChildren: FetchStatusType.COMPLETED, companiesChildren: action.payload }
            })
            .addCase(getAllCompaniesChildren.rejected, state => {
                return { ...state, loadingCompaniesChildren: FetchStatusType.FAILED }
            })
        builder
            .addCase(getAllUsers.pending, state => {
                return { ...state, loadingListUsers: FetchStatusType.LOADING }
            })
            .addCase(getAllUsers.fulfilled, (state, action) => {
                return { ...state, loadingListUsers: FetchStatusType.COMPLETED, listUsers: action.payload }
            })
            .addCase(getAllUsers.rejected, state => {
                return { ...state, loadingListUsers: FetchStatusType.FAILED }
            })
        builder
            .addCase(createUser.pending, state => {
                return { ...state, loadingCreateUser: FetchStatusType.LOADING }
            })
            .addCase(createUser.fulfilled, (state, action) => {
                if (action.payload) {
                    return {
                        ...state,
                        loadingCreateUser: FetchStatusType.COMPLETED,
                        listUsers: [...state.listUsers, action.payload as UserDTO]
                    }
                }
                return { ...state, loadingCreateUser: FetchStatusType.COMPLETED }
            })
            .addCase(createUser.rejected, state => {
                return { ...state, loadingCreateUser: FetchStatusType.FAILED }
            })
        builder
            .addCase(updateUser.pending, state => {
                return { ...state, loadingUpdateUser: FetchStatusType.LOADING }
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                if (action.payload && action.payload.id) {
                    const updatedUser = action.payload as UserDTO
                    return {
                        ...state,
                        loadingUpdateUser: FetchStatusType.COMPLETED,
                        listUsers: state.listUsers.map(user => 
                            user.id === updatedUser.id ? updatedUser : user
                        )
                    }
                }
                return { ...state, loadingUpdateUser: FetchStatusType.COMPLETED }
            })
            .addCase(updateUser.rejected, state => {
                return { ...state, loadingUpdateUser: FetchStatusType.FAILED }
            })
        builder
            .addCase(deleteUser.pending, state => {
                return { ...state, loadingDeleteUser: FetchStatusType.LOADING }
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                return {
                    ...state,
                    loadingDeleteUser: FetchStatusType.COMPLETED,
                    listUsers: state.listUsers.filter(user => user.id !== action.meta.arg)
                }
            })
            .addCase(deleteUser.rejected, state => {
                return { ...state, loadingDeleteUser: FetchStatusType.FAILED }
            })
        builder
            .addCase(exportAllUsers.pending, state => {
                return { ...state, loadingExportAllUser: FetchStatusType.LOADING }
            })
            .addCase(exportAllUsers.fulfilled, state => {
                return { ...state, loadingExportAllUser: FetchStatusType.COMPLETED }
            })
            .addCase(exportAllUsers.rejected, state => {
                return { ...state, loadingExportAllUser: FetchStatusType.FAILED }
            })
        builder
            .addCase(getAllSites.pending, state => {
                return { ...state, loadingGetSites: FetchStatusType.LOADING }
            })
            .addCase(getAllSites.fulfilled, (state, action) => {
                return { ...state, loadingGetSites: FetchStatusType.COMPLETED, listSites: action.payload }
            })
            .addCase(getAllSites.rejected, state => {
                return { ...state, loadingGetSites: FetchStatusType.FAILED }
            })
        builder
            .addCase(exportSiteCompany.pending, state => {
                return { ...state, loadingExportSite: FetchStatusType.LOADING }
            })
            .addCase(exportSiteCompany.fulfilled, state => {
                return { ...state, loadingExportSite: FetchStatusType.COMPLETED }
            })
            .addCase(exportSiteCompany.rejected, state => {
                return { ...state, loadingExportSite: FetchStatusType.FAILED }
            })
        builder
            .addCase(exportSiteById.pending, state => {
                return { ...state, loadingExportSiteById: FetchStatusType.LOADING }
            })
            .addCase(exportSiteById.fulfilled, state => {
                return { ...state, loadingExportSiteById: FetchStatusType.COMPLETED }
            })
            .addCase(exportSiteById.rejected, state => {
                return { ...state, loadingExportSiteById: FetchStatusType.FAILED }
            })
        builder
            .addCase(getAllCatalogs.pending, state => {
                return { ...state, loadingGetCatalog: FetchStatusType.LOADING }
            })
            .addCase(getAllCatalogs.fulfilled, (state, action) => {
                return { ...state, loadingGetCatalog: FetchStatusType.COMPLETED, listCatalogs: action.payload }
            })
            .addCase(getAllCatalogs.rejected, state => {
                return { ...state, loadingGetCatalog: FetchStatusType.FAILED }
            })
        builder
            .addCase(updateCatalogDM.pending, state => {
                return { ...state, loadingDM: FetchStatusType.LOADING }
            })
            .addCase(updateCatalogDM.fulfilled, (state, action) => {
                const updatedCatalog = action.payload
                const updatedCatalogs = state.listCatalogs.map(catalog => (catalog.id === updatedCatalog.id ? updatedCatalog : catalog))
                return {
                    ...state,
                    loadingDM: FetchStatusType.COMPLETED,
                    listCatalogs: updatedCatalogs
                }
            })
            .addCase(updateCatalogDM.rejected, state => {
                return { ...state, loadingDM: FetchStatusType.FAILED }
            })
        builder
            .addCase(updateCatalogYT.pending, state => {
                return { ...state, loadingYT: FetchStatusType.LOADING }
            })
            .addCase(updateCatalogYT.fulfilled, (state, action) => {
                const updatedCatalog = action.payload
                const updatedCatalogs = state.listCatalogs.map(catalog => (catalog.id === updatedCatalog.id ? updatedCatalog : catalog))
                return {
                    ...state,
                    loadingYT: FetchStatusType.COMPLETED,
                    listCatalogs: updatedCatalogs
                }
            })
            .addCase(updateCatalogYT.rejected, state => {
                return { ...state, loadingYT: FetchStatusType.FAILED }
            })
        builder
            .addCase(exportCatalogById.pending, state => {
                return { ...state, loadingExportCatById: FetchStatusType.LOADING }
            })
            .addCase(exportCatalogById.fulfilled, state => {
                return { ...state, loadingExportCatById: FetchStatusType.COMPLETED }
            })
            .addCase(exportCatalogById.rejected, state => {
                return { ...state, loadingExportCatById: FetchStatusType.FAILED }
            })
        builder
            .addCase(exportCatalog.pending, state => {
                return { ...state, loadingExportCat: FetchStatusType.LOADING }
            })
            .addCase(exportCatalog.fulfilled, state => {
                return { ...state, loadingExportCat: FetchStatusType.COMPLETED }
            })
            .addCase(exportCatalog.rejected, state => {
                return { ...state, loadingExportCat: FetchStatusType.FAILED }
            })
        builder
            .addCase(getAllRoles.pending, state => {
                return { ...state, loadingListRole: FetchStatusType.LOADING }
            })
            .addCase(getAllRoles.fulfilled, (state, action) => {
                return { ...state, loadingListRole: FetchStatusType.COMPLETED, listRoles: action.payload }
            })
            .addCase(getAllRoles.rejected, state => {
                return { ...state, loadingListRole: FetchStatusType.FAILED }
            })
        builder
            .addCase(sendResetPwd.pending, state => {
                return { ...state, loadingResetMdp: FetchStatusType.LOADING }
            })
            .addCase(sendResetPwd.fulfilled, state => {
                return { ...state, loadingResetMdp: FetchStatusType.COMPLETED }
            })
            .addCase(sendResetPwd.rejected, state => {
                return { ...state, loadingResetMdp: FetchStatusType.FAILED }
            })
    }
})
// export const {} = ManagementSlice.actions

export const listCompanies = (state: RootState) => state.management.listCompanies
export const loadingCompanies = (state: RootState) => state.management.loadingCompanies
export const companyById = (state: RootState) => state.management.companyById
export const loadingCompanyId = (state: RootState) => state.management.loadingCompanyId
export const companiesChildren = (state: RootState) => state.management.companiesChildren
export const loadingCompaniesChildren = (state: RootState) => state.management.loadingCompaniesChildren
export const loadingListUsers = (state: RootState) => state.management.loadingListUsers
export const listUsers = (state: RootState) => state.management.listUsers
export const loadingCreateUser = (state: RootState) => state.management.loadingCreateUser
export const loadingUpdateUser = (state: RootState) => state.management.loadingUpdateUser
export const loadingDeleteUser = (state: RootState) => state.management.loadingDeleteUser
export const loadingExportAllUser = (state: RootState) => state.management.loadingExportAllUser
export const listSites = (state: RootState) => state.management.listSites
export const loadingGetSites = (state: RootState) => state.management.loadingGetSites
export const loadingExportSite = (state: RootState) => state.management.loadingExportSite
export const loadingExportSiteById = (state: RootState) => state.management.loadingExportSiteById
export const listCatalogs = (state: RootState) => state.management.listCatalogs
export const loadingGetCatalog = (state: RootState) => state.management.loadingGetCatalog
export const loadingDM = (state: RootState) => state.management.loadingDM
export const loadingYT = (state: RootState) => state.management.loadingYT
export const loadingExportCatById = (state: RootState) => state.management.loadingExportCatById
export const loadingExportCat = (state: RootState) => state.management.loadingExportCat
export const loadingListRole = (state: RootState) => state.management.loadingListRole
export const listRoles = (state: RootState) => state.management.listRoles

export default ManagementSlice.reducer
